import Consts from '@/api/Consts';
import Utils from '@/api/Utils';

import { SPOOFING_GNSS_CODES } from '@/utils/spoofingVisibilityHeatmapUtils';


export const GSS_MAP_OVERLAY_FAIL_MODE = {
	anyGnss: 'anyGnss',
	multiGnss: 'multiGnss',
} as const;

export type GssMapOverlayFailMode = typeof GSS_MAP_OVERLAY_FAIL_MODE[keyof typeof GSS_MAP_OVERLAY_FAIL_MODE];

export const GSS_MAP_OVERLAY_FAIL_MODE_SELECTED = GSS_MAP_OVERLAY_FAIL_MODE.anyGnss;
export const GSS_MAP_OVERLAY_THRESHOLD_PERCENT = 75;
export const GSS_MAP_OVERLAY_WINDOW_MINUTES = 30;

export type GssWindowItem = {
	mountpoint: string;
	gnss: string;
	expected_sec: number;
	usable_sec: number;
	median_percent: number;
};

export type GssOverlayGnssDebug = {
	code: string;
	percents: readonly number[];
	median: number | null;
};

export type GssOverlayCollectResult = {
	failingGnssByMountpoint: ReadonlyMap<string, readonly string[]>;
	debugByMountpoint: ReadonlyMap<string, readonly GssOverlayGnssDebug[]>;
};

const indexPercentsByMountpointGnss = (
	items: readonly GssWindowItem[],
): Map<string, Map<string, number[]>> => {
	const result = new Map<string, Map<string, number[]>>();
	for (const item of items) {
		if (!SPOOFING_GNSS_CODES.includes(item.gnss) || item.expected_sec <= 0) {
			continue;
		}
		let mountpointPercents = result.get(item.mountpoint);
		if (mountpointPercents == null) {
			mountpointPercents = new Map();
			result.set(item.mountpoint, mountpointPercents);
		}
		const existing = mountpointPercents.get(item.gnss);
		if (existing == null) {
			mountpointPercents.set(item.gnss, [item.median_percent]);
		} else {
			existing.push(item.median_percent);
		}
	}
	return result;
};

const getGnssDebugForMountpoint = (percentsByGnss: Map<string, number[]>): readonly GssOverlayGnssDebug[] => {
	const result: GssOverlayGnssDebug[] = [];
	const sortedGnss = Consts.sortGnssCodes(
		Array.from(percentsByGnss.keys()).filter(gnss => SPOOFING_GNSS_CODES.includes(gnss)),
	);
	for (const gnss of sortedGnss) {
		const percents = percentsByGnss.get(gnss);
		if (percents == null) {
			continue;
		}
		result.push({
			code: gnss,
			percents: [...percents],
			median: Utils.median(percents),
		});
	}
	return result;
};

const getFailingGnssFromDebug = (debug: readonly GssOverlayGnssDebug[]): readonly string[] => {
	const failing: string[] = [];
	for (const row of debug) {
		if (row.median != null && row.median < GSS_MAP_OVERLAY_THRESHOLD_PERCENT) {
			failing.push(row.code);
		}
	}
	return failing;
};

const shouldIncludeMountpoint = (
	failingGnss: readonly string[],
	mode: GssMapOverlayFailMode,
): boolean => {
	if (mode === GSS_MAP_OVERLAY_FAIL_MODE.anyGnss) {
		return failingGnss.length >= 1;
	}
	return failingGnss.length >= 2;
};

export const collectGssOverlayStations = (
	items: readonly GssWindowItem[],
	mode: GssMapOverlayFailMode = GSS_MAP_OVERLAY_FAIL_MODE_SELECTED,
): GssOverlayCollectResult => {
	const indexed = indexPercentsByMountpointGnss(items);
	const failingGnssByMountpoint = new Map<string, readonly string[]>();
	const debugByMountpoint = new Map<string, readonly GssOverlayGnssDebug[]>();
	for (const [mountpoint, percentsByGnss] of indexed) {
		const gnssDebug = getGnssDebugForMountpoint(percentsByGnss);
		debugByMountpoint.set(mountpoint, gnssDebug);
		const failingGnss = getFailingGnssFromDebug(gnssDebug);
		if (shouldIncludeMountpoint(failingGnss, mode)) {
			failingGnssByMountpoint.set(mountpoint, failingGnss);
		}
	}
	return {
		failingGnssByMountpoint,
		debugByMountpoint,
	};
};
